import { useEffect, useState } from 'react'
import { checkReset, completeReset } from '../lib/api'
import { Logo } from '../components/Logo'

/**
 * The far end of a password reset link.
 *
 * The link is checked before the form is shown, so somebody who finds an old
 * reset email is told it has expired rather than typing a new password into a
 * box that was never going to work.
 *
 * No session is issued on success. Completing a reset proves control of a
 * mailbox, not of the account — so it ends at the sign-in screen with the new
 * password, which is one more thing to type and one fewer way for a stolen
 * inbox to become a live session.
 */
export function ResetPasswordView({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<'checking' | 'ready' | 'bad' | 'done'>('checking')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void checkReset(token).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setEmail(result.email ?? '')
        setState('ready')
      } else {
        setError(result.error)
        setState('bad')
      }
    })
    return () => {
      cancelled = true
    }
  }, [token])

  const tooShort = password.length > 0 && password.length < 12
  const mismatch = confirm.length > 0 && password !== confirm
  const canSubmit = password.length >= 12 && password === confirm && !busy

  async function submit() {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    const result = await completeReset(token, password)
    setBusy(false)
    if (!result.ok) {
      setError(result.error)
      return
    }
    setState('done')
  }

  return (
    <div className="signin">
      <div className="signin-head">
        <Logo size={44} />
      </div>

      {state === 'checking' && <p className="muted">Checking that link…</p>}

      {state === 'bad' && (
        <>
          <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
            That link has expired
          </h1>
          <p className="lede">{error}</p>
          <button type="button" className="btn is-primary" onClick={onDone}>
            Back to sign in
          </button>
        </>
      )}

      {state === 'done' && (
        <>
          <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
            Password changed
          </h1>
          <p className="lede">
            Every device that was signed in has been signed out, including any you did not
            recognise. Sign in again with the new password.
          </p>
          <button type="button" className="btn is-primary" onClick={onDone}>
            Sign in
          </button>
        </>
      )}

      {state === 'ready' && (
        <>
          <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
            Choose a new password
          </h1>
          <p className="lede" style={{ marginBottom: '1.4rem' }}>
            For {email}. Twelve characters or more — a short sentence you will remember beats
            something clever you will not.
          </p>

          {error && (
            <div className="notice is-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <label className="field">
            <span className="field-label">New password</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {tooShort && (
              <span className="muted" style={{ fontSize: '0.84rem' }}>
                {12 - password.length} more to go.
              </span>
            )}
          </label>

          <label className="field">
            <span className="field-label">Again, to be sure</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit()
              }}
            />
            {mismatch && (
              <span className="muted" style={{ fontSize: '0.84rem' }}>
                These two do not match.
              </span>
            )}
          </label>

          <button
            type="button"
            className="btn is-primary"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {busy ? 'Changing it…' : 'Change my password'}
          </button>
        </>
      )}
    </div>
  )
}
